import React from 'react';
import ReactDOM from 'react-dom';
import { useAppConfig } from '../AppConfig';
import { useSeoData } from '../data/SeoData';
import { useMetaTagsClient } from './MetaTagsClient';

export type MetaTagsState = {
    canonical?: string;
    description?: string;
    robots?: string;
    title?: string;
    breadcrumbs?: string;
};

const META_TAGS = {
    alternate: {
        component: (key: string, content: string, locale: string) => (
            <link key={key} rel="alternate" hrefLang={locale} href={content} />
        ),
        key: 'alternate',
        selector: 'link[rel="alternate"]',
    },
    canonical: {
        component: (key: string, content: string) => (
            <link key={key} rel="canonical" href={content} />
        ),
        key: 'canonical',
        selector: 'link[rel="canonical"]',
    },
    description: {
        component: (key: string, content: string) => (
            <meta key={key} name="description" content={content} />
        ),
        key: 'description',
        selector: 'meta[name="description"]',
    },
    robots: {
        component: (key: string, content: string) => (
            <meta key={key} name="robots" content={content} />
        ),
        key: 'robots',
        selector: 'meta[name="robots"]',
    },
    title: {
        component: (key: string, content: string) => (
            <title key={key}>{content}</title>
        ),
        key: 'title',
        selector: 'title',
    },
    breadcrumbs: {
        component: (key: string, content: string) => (
            <script type="application/ld+json" id="seo-breadcrumbs" key={key}>
                {content}
            </script>
        ),
        key: 'breadcrumbs',
        selector: 'script[id=seo-breadcrumbs]',
    },
};

export function MetaTags() {
    const [isMounted, setIsMounted] = React.useState(false);

    React.useEffect(function cleanupServerRender() {
        Object.values(META_TAGS)
            .map((value) => value.selector)
            .forEach(removeServerTag);
        setIsMounted(true);
    }, []);

    const { locale } = useAppConfig();
    const { metaTagsClient } = useMetaTagsClient();
    const { seoData } = useSeoData();

    if (seoData) {
        metaTagsClient.canonical = seoData.data.canonical;
        metaTagsClient.description = seoData.data.meta;
        metaTagsClient.robots = seoData.data.robots;
        metaTagsClient.title = seoData.data.title;
        metaTagsClient.breadcrumbs = JSON.stringify(seoData.data.breadcrumbs);
    }

    const tags = React.useMemo(() => {
        const tags: JSX.Element[] = [];
        if (seoData) {
            tags.push(
                META_TAGS['alternate'].component(
                    META_TAGS['alternate'].key,
                    seoData.data.canonical,
                    locale,
                ),
            );
            tags.push(
                META_TAGS['canonical'].component(
                    META_TAGS['canonical'].key,
                    seoData.data.canonical,
                ),
            );
            tags.push(
                META_TAGS['description'].component(
                    META_TAGS['description'].key,
                    seoData.data.meta,
                ),
            );
            tags.push(
                META_TAGS['robots'].component(
                    META_TAGS['robots'].key,
                    seoData.data.robots,
                ),
            );
            tags.push(
                META_TAGS['title'].component(
                    META_TAGS['title'].key,
                    seoData.data.title,
                ),
            );
            tags.push(
                META_TAGS['breadcrumbs'].component(
                    META_TAGS['breadcrumbs'].key,
                    JSON.stringify(seoData.data.breadcrumbs),
                ),
            );
        }
        return tags;
    }, [locale, seoData]);

    return isMounted ? ReactDOM.createPortal(tags, document.head) : null;
}

function removeServerTag(selector: string) {
    const element = document.head.querySelector(selector);
    if (element) {
        element.parentNode?.removeChild(element);
    }
}
